import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Users, TrendingUp, Zap, Phone, BarChart3, Bot, Globe } from 'lucide-react';
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const HERO_IMAGE = "https://images.pexels.com/photos/7653461/pexels-photo-7653461.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const TEAM_IMAGE = "https://images.pexels.com/photos/7580644/pexels-photo-7580644.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Home = () => {
  const services = [
    {
      icon: <Target className="w-6 h-6" />,
      title: "Timeshare OPC Marketing",
      description: "Expert OPC marketers driving qualified resort tours through strategic engagement.",
      color: "bg-[#1E3A8A]",
      link: "/timeshare-opc"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Lead Generation",
      description: "Advanced systems to capture and convert high-quality leads for your business.",
      color: "bg-[#0D9488]",
      link: "/services"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Community Marketing",
      description: "Building powerful partnerships with local businesses and communities.",
      color: "bg-[#EA580C]",
      link: "/services"
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Call Center Services",
      description: "Professional appointment setting and outreach that delivers results.",
      color: "bg-[#DC2626]",
      link: "/services"
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Digital Advertising",
      description: "Google, Social Media, and CTV campaigns optimized for maximum ROI.",
      color: "bg-[#1E3A8A]",
      link: "/services"
    },
    {
      icon: <Bot className="w-6 h-6" />,
      title: "AI Tools",
      description: "Cutting-edge AI solutions including Ghost Assistant to elevate your marketing.",
      color: "bg-[#0D9488]",
      link: "/services"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Web Design & Hosting",
      description: "World-class websites on enterprise-grade infrastructure with AI-powered design.",
      color: "bg-[#1E3A8A]",
      link: "/web-design"
    }
  ];

  const stats = [
    { value: "500+", label: "Clients Served" },
    { value: "10M+", label: "Leads Generated" },
    { value: "98%", label: "Client Retention" },
    { value: "24/7", label: "Support Available" }
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="container-max mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="animate-fade-in">
              <span className="inline-block px-4 py-1.5 bg-[#1E3A8A]/10 text-[#1E3A8A] text-xs font-bold uppercase tracking-wider rounded-full mb-6">
                Strategic Marketing Excellence
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 mb-6 leading-[1.1]">
                Unleash the Power of{' '}
                <span className="text-gradient">Strategic Marketing</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 max-w-xl leading-relaxed">
                Your vision, Our strategies, We win together. At Prestigious Marketing Inc, 
                we transform businesses through data-driven brilliance and unstoppable innovation.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/partner">
                  <Button 
                    size="lg" 
                    className="bg-[#1E3A8A] hover:bg-[#1e40af] text-white px-8 py-6 text-base font-medium"
                    data-testid="hero-cta-partner"
                  >
                    Become a Partner
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-slate-300 text-slate-700 hover:bg-slate-100 px-8 py-6 text-base font-medium"
                    data-testid="hero-cta-services"
                  >
                    Explore Services
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative animate-fade-in stagger-2">
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={HERO_IMAGE}
                  alt="Strategic marketing team"
                  className="w-full h-auto object-cover"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#EA580C] rounded-2xl -z-10 opacity-80" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#1E3A8A] rounded-2xl -z-10 opacity-80" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#0F172A] py-12">
        <div className="container-max mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-slate-400 text-sm font-medium uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-white" data-testid="services-section">
        <div className="container-max mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
              What We Do
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Marketing Solutions That Drive Results
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From timeshare OPC marketing to cutting-edge AI tools, we deliver comprehensive 
              solutions tailored to your business needs.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {services.map((service, idx) => (
              <Link to={service.link} key={idx}>
                <Card className="h-full border-slate-200 hover:border-[#1E3A8A] transition-all card-hover bg-white" data-testid={`service-card-${idx}`}>
                  <CardContent className="p-6 lg:p-8">
                    <div className={`w-12 h-12 ${service.color} text-white rounded-xl flex items-center justify-center mb-5`}>
                      {service.icon}
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button 
                variant="outline" 
                size="lg"
                className="border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white"
                data-testid="view-all-services"
              >
                View All Services
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-max mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative">
              <img 
                src={TEAM_IMAGE}
                alt="Our team"
                className="rounded-2xl shadow-lg w-full"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#EA580C] text-white p-6 rounded-xl shadow-lg max-w-[200px]">
                <div className="text-3xl font-black mb-1">15+</div>
                <div className="text-sm opacity-90">Years of Excellence</div>
              </div>
            </div>
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
                About Us
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6">
                Architects of Success
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                At Prestigious Marketing Inc, we are architects of success, forging a future where 
                data-driven brilliance and unstoppable innovation converge. We're not your ordinary 
                team – we're trailblazers who shatter boundaries and redefine success.
              </p>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Armed with the 'Hustle' mentality, our group of passionate experts turns ambition 
                into action, leaving no stone unturned to make your dreams a thriving reality.
              </p>
              <Link to="/about">
                <Button 
                  className="bg-[#1E3A8A] hover:bg-[#1e40af]"
                  data-testid="learn-more-about"
                >
                  Learn More About Us
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Ghost AI Section */}
      <section className="section-padding bg-[#0F172A]" data-testid="ghost-section">
        <div className="container-max mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 lg:p-12 border border-slate-700">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-full bg-slate-800 border-2 border-[#EA580C] flex items-center justify-center overflow-hidden">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_8a1d6c9e-dd92-45ef-96ba-a4a899e05fe9/artifacts/4lw5n36q_image.png"
                        alt="Ghost"
                        className="w-12 h-12 object-contain"
                      />
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg">Ghost Assistant</h3>
                      <p className="text-slate-400 text-sm">AI-Powered by Prestigious</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                      <p className="text-slate-300 text-sm">
                        "How can Ghost help my business?"
                      </p>
                    </div>
                    <div className="bg-[#1E3A8A]/30 rounded-xl p-4 border border-[#1E3A8A]/50">
                      <p className="text-slate-200 text-sm">
                        Ghost can answer questions about our services 24/7, help qualify leads, 
                        guide visitors to the right solutions, and provide instant support. 
                        Try me out - click the Ghost icon in the bottom right corner!
                      </p>
                    </div>
                  </div>
                </div>
                {/* Decorative glow */}
                <div className="absolute -inset-4 bg-[#EA580C]/10 rounded-3xl blur-2xl -z-10" />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <span className="inline-block px-4 py-1.5 bg-[#EA580C]/20 text-[#EA580C] text-xs font-bold uppercase tracking-wider rounded-full mb-6">
                Introducing Ghost
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-6">
                Meet Your AI-Powered Marketing Assistant
              </h2>
              <p className="text-slate-300 mb-6 leading-relaxed">
                Ghost is our flagship AI assistant, powered by cutting-edge artificial intelligence 
                and trained specifically on Prestigious Marketing's services and expertise. Available 
                24/7 to help visitors and potential partners.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  "Answers questions about all our services instantly",
                  "Helps qualify leads and guide visitors to solutions",
                  "Available 24/7 - never miss a potential partner",
                  "Powered by advanced AI technology"
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#0D9488] flex items-center justify-center flex-shrink-0">
                      <Zap className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-slate-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              <p className="text-slate-400 text-sm mb-6">
                <span className="text-[#EA580C] font-semibold">Try it now!</span> Click the Ghost 
                icon in the bottom right corner to start a conversation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-gradient py-20 lg:py-28">
        <div className="container-max mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-6 tracking-tight">
            Ready to Dominate Your Market?
          </h2>
          <p className="text-slate-300 text-lg mb-10 max-w-2xl mx-auto">
            Join hundreds of successful businesses who've partnered with us to achieve 
            extraordinary results. Let's ignite your success together.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/partner">
              <Button 
                size="lg" 
                className="bg-[#EA580C] hover:bg-[#c2410c] text-white px-8 py-6 text-base font-medium"
                data-testid="cta-partner"
              >
                Become a Partner
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base font-medium"
                data-testid="cta-contact"
              >
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto text-center">
          <blockquote className="max-w-3xl mx-auto">
            <p className="text-2xl sm:text-3xl font-light text-slate-700 italic mb-6">
              "The aim of marketing is to know and understand the customer so well 
              the product or service fits him and sells itself."
            </p>
            <cite className="text-slate-500 not-italic font-medium">— Peter Drucker</cite>
          </blockquote>
        </div>
      </section>
    </div>
  );
};

export default Home;
